import { functions } from '../firebase/firebase';
import { httpsCallable } from 'firebase/functions';

// Create callable function references
// AI functions need longer timeouts (5 minutes) to handle Claude 4.5 processing time
const processAssessmentForm = httpsCallable(functions, 'processAssessmentForm', { timeout: 300000 });
const getAssessmentHistoryFunction = httpsCallable(functions, 'getAssessmentHistory');
const getAssessmentByIdFunction = httpsCallable(functions, 'getAssessmentById');
const getAssessmentsByGoalFunction = httpsCallable(functions, 'getAssessmentsByGoal');
const deleteAssessmentFunction = httpsCallable(functions, 'deleteAssessment');

// New credit plan functions
// AI functions need longer timeouts (5 minutes) to handle Claude 4.5 processing time
const generateCreditPlanFunction = httpsCallable(functions, 'generateCreditPlan', { timeout: 300000 });
const populateStepComprehensiveFunction = httpsCallable(functions, 'populateStepComprehensive', { timeout: 300000 });
const populateStepsBatchFunction = httpsCallable(functions, 'populateStepsBatch', { timeout: 300000 });
const getCreditPlanFunction = httpsCallable(functions, 'getCreditPlan');
const updateCreditPlanNodeFunction = httpsCallable(functions, 'updateCreditPlanNode');

// Utility function to handle Firebase function responses
const handleFirebaseResponse = (result, functionName) => {
  if (!result.data) {
    throw new Error(`No data returned from ${functionName}`);
  }
  return result.data.success && result.data.data ? result.data.data : result.data;
};

/**
 * Generate a credit plan using the two-phase approach
 * @param {Object} assessmentData - The assessment data to submit
 * @param {string} assessmentData.formId - The form ID
 * @param {string} assessmentData.subcategory - The subcategory
 * @param {Object} assessmentData.goal - The goal object
 * @param {Object} assessmentData.formData - The form field data
 * @param {string} assessmentData.submittedAt - ISO timestamp
 * @returns {Promise<Object>} - The response from the Firebase function
 */
export const generateCreditPlan = async (assessmentData) => {
  const result = await generateCreditPlanFunction(assessmentData);
  return handleFirebaseResponse(result, 'generateCreditPlan');
};



/**
 * Get a credit plan by ID
 * @param {string} planId - The credit plan ID
 * @returns {Promise<Object>} - The credit plan data
 */
export const getCreditPlan = async (planId) => {
  const result = await getCreditPlanFunction({ planId });
  return handleFirebaseResponse(result, 'getCreditPlan');
};

/**
 * Populate comprehensive expert-level content for a specific step
 * @param {Object} stepData - The step data and context
 * @param {string} stepData.planId - The plan ID
 * @param {string} stepData.stepId - The step ID
 * @param {Object} stepData.stepData - The current step data
 * @param {Object} stepData.goal - The goal object
 * @param {Object} stepData.formData - The form field data
 * @param {string} stepData.subcategory - The subcategory
 * @returns {Promise<Object>} - The response from the Firebase function
 */
export const populateStepComprehensive = async (stepData) => {
  const result = await populateStepComprehensiveFunction(stepData);
  return handleFirebaseResponse(result, 'populateStepComprehensive');
};

/**
 * Batch populate multiple steps in a single API call (reduces calls by ~60%)
 * @param {Object} batchData - The batch data
 * @param {string} batchData.planId - The plan ID
 * @param {Array} batchData.steps - Array of { stepId, stepData }
 * @param {Object} batchData.goal - The goal object
 * @param {Object} batchData.formData - The form field data
 * @param {string} batchData.subcategory - The subcategory
 * @param {string} batchData.previousStepsSummary - Summary of previous steps
 * @returns {Promise<Object>} - The response from the Firebase function
 */
export const populateStepsBatch = async (batchData) => {
  const result = await populateStepsBatchFunction(batchData);
  return handleFirebaseResponse(result, 'populateStepsBatch');
};

/**
 * Submit assessment form data to Firebase Functions for OpenAI analysis
 * @param {Object} assessmentData - The assessment data to submit
 * @param {string} assessmentData.formId - The form ID
 * @param {string} assessmentData.subcategory - The subcategory
 * @param {Object} assessmentData.goal - The goal object
 * @param {Object} assessmentData.formData - The form field data
 * @param {string} assessmentData.submittedAt - ISO timestamp
 * @returns {Promise<Object>} - The response from the Firebase function
 */
export const submitAssessmentForm = async (assessmentData) => {
  const result = await processAssessmentForm(assessmentData);
  return result.data;
};

/**
 * Get assessment history for a user
 * @param {Object} params - Parameters for getting assessment history
 * @param {number} params.limit - Number of assessments to retrieve (default: 10)
 * @param {number} params.offset - Number of assessments to skip (default: 0)
 * @returns {Promise<Object>} - Object containing assessments array and total count
 */
export const getAssessmentHistory = async (params = {}) => {
  const result = await getAssessmentHistoryFunction(params);
  return result.data;
};

/**
 * Get a specific assessment by ID
 * @param {string} assessmentId - The assessment ID
 * @returns {Promise<Object>} - The assessment data
 */
export const getAssessmentById = async (assessmentId) => {
  const result = await getAssessmentByIdFunction({ assessmentId });
  return result.data;
};

/**
 * Get assessments by goal for the current user
 * @param {Object} params - Parameters for getting assessments by goal
 * @param {string} params.goalId - The goal ID to filter by
 * @param {string} params.subcategory - The subcategory to filter by
 * @returns {Promise<Object>} - Object containing assessments array and total count
 */
export const getAssessmentsByGoal = async (params = {}) => {
  const result = await getAssessmentsByGoalFunction(params);
  return result.data;
};

/**
 * Delete an assessment by ID
 * @param {string} assessmentId - The assessment ID to delete
 * @returns {Promise<Object>} - Success message and assessment ID
 */
export const deleteAssessment = async (assessmentId) => {
  const result = await deleteAssessmentFunction({ assessmentId });
  return result.data;
};

/**
 * Update a specific node in a credit plan
 * @param {string} planId - The credit plan ID
 * @param {string} stepId - The step ID to update
 * @param {Object} updates - The fields to update (notes, deadline, status, todos, etc.)
 * @returns {Promise<Object>} - Success message and updated data
 */
export const updateCreditPlanNode = async (planId, stepId, updates) => {
  console.log('assessmentService.updateCreditPlanNode called with:', { planId, stepId, updates });
  try {
    const result = await updateCreditPlanNodeFunction({ planId, stepId, updates });
    console.log('assessmentService.updateCreditPlanNode result:', result);
    return handleFirebaseResponse(result, 'updateCreditPlanNode');
  } catch (error) {
    console.error('assessmentService.updateCreditPlanNode error:', error);
    throw error;
  }
}; 